// Phase 999.6 Plan 03 — One-line InputData summary for the UserLogViewer.
//
// summariseInputData(actionName, inputDataStr) → string
//
// Converts a raw InputData JSON string into a short human-readable line
// appropriate for a table cell. Falls back to the raw string on JSON parse
// failure, and to '' for null/undefined/empty input.
//
// Rules:
//  - Wrap JSON.parse in try/catch — return raw string on failure (RESEARCH Pitfall 2)
//  - Keep it defensive: any missing JSON key degrades gracefully (no exceptions)
//  - No console.log
//  - Named export only — no default export, no React import

/**
 * Returns a short human-readable one-liner summarising the InputData for a
 * given userlog row.
 *
 * @param {string} actionName  - The row's actionName (e.g. 'SubSequence Insert').
 * @param {string} inputDataStr - The raw InputData column value (JSON string or '').
 * @returns {string} A one-line summary, or the raw string if unparsable.
 */
export function summariseInputData(actionName, inputDataStr) {
    if (inputDataStr === null || inputDataStr === undefined || inputDataStr === '') {
        return '';
    }

    let data;
    try {
        data = JSON.parse(inputDataStr);
    } catch (_) {
        // Not valid JSON — return as-is (handles legacy or forensic non-JSON values).
        return String(inputDataStr);
    }

    if (data === null || typeof data !== 'object') {
        return String(inputDataStr);
    }

    switch (actionName) {
        case 'SubSequence Insert':
        case 'SubSequence Rename':
            return data.Name != null ? 'Name: ' + data.Name : '';

        case 'SubSequence Archive':
        case 'SubSequence Delete':
            return data.subsequenceID != null ? 'ID: ' + data.subsequenceID : '';

        case 'SubLocation Insert':
        case 'SubLocation Rename':
            return data.Name != null ? 'Name: ' + data.Name : '';

        case 'SubLocation Archive':
        case 'SubLocation Delete':
            return data.sublocationID != null ? 'ID: ' + data.sublocationID : '';

        case 'Sequence Insert':
        case 'Sequence Rename':
            return data.Name != null ? 'Name: ' + data.Name : '';

        case 'Sequence Archive':
        case 'Sequence Delete':
            return data.sequenceID != null ? 'ID: ' + data.sequenceID : '';

        case 'Plan File Upload': {
            // InputData has encrypted filename + data fields. Show a safe placeholder.
            const fn = data.filename;
            if (fn == null) return 'File: (encrypted)';
            // If filename looks like a Base64 string it is encrypted — show placeholder.
            const looksEncrypted = /^[A-Za-z0-9+/=]{20,}$/.test(String(fn).trim());
            return looksEncrypted ? 'File: (encrypted)' : 'File: ' + fn;
        }

        case 'Plan Upload Submit New':
        case 'Plan Upload Submit Update':
        case 'Redrill Upload Submit': {
            const id = data.subsequenceID ?? data.planid;
            return id != null ? 'ID: ' + id : '';
        }

        case 'Plan Export Data': {
            const planID = data.planID;
            const type = data.type;
            if (planID != null && type != null) return 'Plan ' + planID + ' (' + type + ')';
            if (planID != null) return 'Plan ' + planID;
            return '';
        }

        case 'Review Insert':
            return data.DispatchID != null ? 'Dispatch: ' + data.DispatchID : '';

        case 'Review Reset Assignments':
            return data.DispatchID != null ? 'Dispatch: ' + data.DispatchID : '';

        case 'Review Approve':
            return ''; // body varies; show nothing rather than raw JSON

        case 'login':
            return '—'; // em dash — no meaningful data for auth rows

        default:
            // For forensic tokens (PushPlan / Update2 / dispatch-published) and
            // any future ActionName not mapped above, return empty — the caller can
            // expose the expand control to show the raw JSON.
            return '';
    }
}
